import React, { useState, useContext, useLayoutEffect } from 'react';
import { useHistory, NavLink } from 'react-router-dom';
import axios from 'axios';
import * as QueryString from 'query-string';
import { AppContext } from 'AppContext';
import ReCAPTCHA from "react-google-recaptcha";

const SignUp = (props) => {

    // Retrieve global configuration from application context:s
    const { config } = useContext(AppContext);
    // Retrieve history router for redirecting the user:
    const [history] = useState(useHistory());

    //const [state, setState] = useState({ busy: false, event: "ready", data: { foo: "bar"}});
    const [busy, setBusy] = useState(false);

    const params = QueryString.parse(props.location.search);

    const [lastName, setLastName] = useState(params.lastname ? params.lastname : "");
    const [firstName, setFirstName] = useState(params.firstname ? params.firstname : "");
    const [company, setCompany] = useState("");
    const [email, setEmail] = useState(params.email ? params.email : "");
    const [phone, setPhone] = useState(params.phone ? params.phone : "");
    const [password, setPassword] = useState("");

    const [error, setError] = useState("");
    const [retries, set_retries] = useState(-1);
    const [retry_timer, set_retry_timer] = useState(-1);

    const recaptchaRef = React.useRef();

    // Workaround to make react-google-recaptcha play nice with react-router-dom
    useLayoutEffect(() => {
        return () => {
            Array.prototype.slice.call(document.getElementsByTagName('IFRAME')).forEach(element => {
                if (element.src.indexOf('www.google.com/recaptcha') > -1 && element.parentNode) {
                element.parentNode.removeChild(element);
                }
            });
        };
    }, []);

    function onSubmitWithReCAPTCHA(e) {
        setBusy(true);
        e.preventDefault();
        recaptchaRef.current.execute();
    }
    
    // Must use ReCAPTCHA onChange as most reliable token source
    function onSuccess(token) {
        if (token === null) {
            setBusy(false);
            recaptchaRef.current.reset();
        } else {
            executeSubmit(token);
        }
    }

    function executeSubmit(token){
        setError("");
        const action = async () => {
            try {
                // Submit the form to the server:
                await axios.post(config.api.base + "/users", {
                    firstName   : firstName,
                    lastName    : lastName,
                    company     : company,
                    password    : password,
                    phone       : phone,
                    email       : email,
                    token       : token,
                })
                // Redirect the user to the sign-in page:
                setBusy(false);
                history.push("/sign-up/complete");
            } catch(x) {
                // setTimeout used due to open issue: https://github.com/dozoisch/react-google-recaptcha/issues/129
                setTimeout(() => recaptchaRef.current.reset(), 500);
                setBusy(false);
                setError(x.response.data.message);
                if (x.response.status === 429) {
                    set_retry_timer(x.response.data.retryAfter);
                } else {
                    set_retry_timer(-1);
                }
                set_retries(x.response.data.remaining);
            }
        }
        action();
    }

    function onChangeFirstName(e) {
        setFirstName(e.target.value);
    }

    function onChangeLastName(e) {
        setLastName(e.target.value);
    }

    function onChangeCompany(e) {
        setCompany(e.target.value);
    }

    function onChangePhone(e) {
        setPhone(e.target.value);
    }

    function onChangeEmail(e) {
        setEmail(e.target.value);
    }

    function onChangePassword(e) {
        setPassword(e.target.value);
    }
    
    const retries_or_retry = retries === 1 ? "retry" : "retries";
    const retry_message = "You have " + retries + " " + retries_or_retry + " remaining.";
    
    const retry_timer_mins = Math.floor(retry_timer / 60);
    const retry_timer_secs = retry_timer % 60;
    const retry_timer_message = "You may try again in " + retry_timer_mins + " minutes and " + retry_timer_secs + " seconds.";

    return(
        <div className="min-h-screen flex justify-center bg-gray-50 pt-24 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        Create a new account
                    </h2>
                </div>
                <form className="mt-8 space-y-6" onSubmit={onSubmitWithReCAPTCHA}>
                    { !!error &&
                        <div className="errorBox flex-col">
                            <p>{error}</p>
                            { retries > -1 &&
                                <p>{retry_message}</p>
                            }
                            { retry_timer > -1 &&
                                <p>{retry_timer_message}</p>
                            }
                        </div>
                    }
                    <div className={"flex w-full space-x-6"}>
                        <div className={"w-full"}>
                            <label htmlFor="first-name">First name</label>
                            <input id="first-name" name="firstName" error={"true"}  type="text" autoComplete="given-name" value={firstName} required
                                   className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                                   placeholder="First name"
                                   onChange={onChangeFirstName}
                            />
                        </div>
                        <div className={"w-full"}>
                            <label htmlFor="last-name">Last name</label>
                            <input id="last-name" name="lastName" type="text" autoComplete="family-name" required
                                   value={lastName}
                                   onChange={onChangeLastName}
                                   className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                                   placeholder="Last name"/>
                        </div>
                    </div>
                    <div>
                        <label htmlFor="company">Company</label>
                        <input id="company" name="company" type="text" autoComplete="company"
                               required
                               value={company}
                               onChange={onChangeCompany}
                               className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                               placeholder="Company"/>
                    </div>
                    <div>
                        <label htmlFor="email">Email address</label>
                        <input id="email" name="email" type="email" autoComplete="email"
                               required
                               value={email}
                               onChange={onChangeEmail}
                               className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                               placeholder="user@address.com"/>
                    </div>                   
                    <div>
                        <label htmlFor="phone">Phone number&nbsp;
                            <span className='italic text-gray-700'>(country code and extension optional)</span>
                        </label>
                        <input id="phone" name="email" type="tel" autoComplete="phone"
                               required
                               value={phone}
                               onChange={onChangePhone}
                               className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                               pattern="^(\+\d{1,3} )?\d{3}-\d{3}-\d{4}( x [\d]+)?$"
                               placeholder="+1 111-111-1111 x 11"/>
                    </div>
                    <div>
                        <label htmlFor="password">Password</label>
                        <input id="password" name="password" type="password"
                               required
                               value={password}
                               onChange={onChangePassword}
                               className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                               placeholder="Password"/>
                    </div>
                    <div>
                        <button disabled={busy} type={"submit"} className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50">
                            Sign Up
                        </button>
                    </div>
                    <div className="flex items-center justify-center">
                        <p className="mt-2 text-center text-sm text-gray-600">
                            Already have an account?
                            <NavLink to={"/sign-in"} className="ml-1 font-medium text-indigo-600 hover:text-indigo-500">
                                Click here to login.
                            </NavLink>
                        </p>
                    </div>
                    <ReCAPTCHA
                        ref={recaptchaRef}
                        size="invisible"
                        sitekey={config.recaptcha.key}
                        onChange={onSuccess}
                    />
                </form>
            </div>
        </div>
    )
}

export default SignUp;